import { ROOT_ID } from '../config/packages';

// Format number with commas
export const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '0';
  
  const number = parseFloat(num);
  if (isNaN(number)) return '0';
  
  return number.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Format compact number (1K, 1M, etc)
export const formatCompact = (num) => {
  if (num === null || num === undefined) return '0';
  
  const number = parseFloat(num);
  if (isNaN(number)) return '0';
  
  if (number >= 1000000) {
    return (number / 1000000).toFixed(2) + 'M';
  }
  if (number >= 1000) {
    return (number / 1000).toFixed(2) + 'K';
  }
  return number.toFixed(2);
};

// Format currency
export const formatCurrency = (num, symbol = 'USDT') => {
  return `${formatNumber(num)} ${symbol}`;
};

// Format a user ID for display - the contract root (1000) is never shown
export const formatUserId = (id) => {
  const n = Number(id?.toString ? id.toString() : id);
  if (!n) return '-';
  if (n === ROOT_ID) return 'Direct';
  return `#${n}`;
};

// Shorten address
export const shortenAddress = (address, chars = 4) => {
  if (!address) return '';
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
};

// Format date
export const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format time
export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format date and time
export const formatDateTime = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp * 1000);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format relative time
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  
  const now = Date.now();
  const date = timestamp * 1000;
  const diff = now - date;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 7) {
    return formatDate(timestamp);
  }
  if (days > 0) {
    return `${days}d ago`;
  }
  if (hours > 0) {
    return `${hours}h ago`;
  }
  if (minutes > 0) {
    return `${minutes}m ago`;
  }
  return 'Just now';
};

// Format countdown
export const formatCountdown = (timestamp) => {
  if (!timestamp) return '00:00:00';
  
  const now = Math.floor(Date.now() / 1000);
  const diff = timestamp - now;
  
  if (diff <= 0) return '00:00:00';
  
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Format percentage
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined) return '0%';
  
  const number = parseFloat(value);
  if (isNaN(number)) return '0%';
  
  return `${number.toFixed(decimals)}%`;
};

// Format basis points to percentage
export const bpsToPercent = (bps) => {
  if (!bps) return '0%';
  return `${(bps / 100).toFixed(2)}%`;
};

// Calculate percentage
export const calculatePercentage = (part, total) => {
  if (!total || total === 0) return 0;
  return (part / total) * 100;
};
